/**
 * src/utils/numberUtils.js
 *
 * Utility functions for percent-change formatting.
 */

/**
 * Safely convert input to a finite Number, or return null if it cannot be converted.
 * Accepts numbers, numeric strings, BigDecimal-like strings, etc.
 * @param {*} v
 * @returns {number|null}
 */
function toNumberOrNull(v) {
  if (v === null || v === undefined) return null;
  // If it's already a number
  if (typeof v === "number") {
    return Number.isFinite(v) ? v : null;
  }
  // Try to coerce strings and other types
  try {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

/**
 * Compute numeric percent change from previous -> current.
 * Returns null when percent change is not computable (e.g., previous is null/undefined or previous === 0 and current !== 0).
 * If previous === 0 and current === 0 -> returns 0.
 *
 * @param {*} current
 * @param {*} previous
 * @returns {number|null} percent change as a number (e.g. 5.25 for +5.25%), or null if not computable
 */
export function getPercentChangeNumber(current, previous) {
  const c = toNumberOrNull(current);
  const p = toNumberOrNull(previous);

  // previous missing => cannot compute
  if (p === null) return null;

  // handle previous === 0
  if (p === 0) {
    // both zero => 0%
    if (c === 0 || c === null) return 0;
    // previous is zero but current non-zero => undefined / not computable (avoid divide-by-zero)
    return null;
  }

  const change = (((c ?? 0) - p) / p) * 100;
  if (!Number.isFinite(change)) return null;
  return change;
}

/**
 * Format percent change as a string with sign and percent symbol.
 *
 * - If previous is null/undefined or percent is not computable (e.g., previous === 0 && current !== 0) returns "N/A".
 * - If percent is computable, returns a string like "+5.25%" or "-3.00%".
 * - By default rounds to 2 decimal places; pass options.decimals to change.
 *
 * @param {*} current
 * @param {*} previous
 * @param {Object} options
 * @param {number} options.decimals - number of decimal places (default 2)
 * @param {string} options.naString - string to return when not computable (default "N/A")
 * @returns {string}
 */
export function formatPercentChange(current, previous, options = {}) {
  const { decimals = 2, naString = "N/A" } = options;
  const num = getPercentChangeNumber(current, previous);

  if (num === null) {
    return naString;
  }

  // Round to specified decimals (preserve sign)
  const rounded = Number(num.toFixed(decimals));

  // Display + for non-negative numbers (including 0)
  const sign = rounded >= 0 ? "+" : ""; // negative numbers already carry '-'
  // Use absolute formatting but keep sign logic simple by prepending '+' when >= 0
  const formatted = `${sign}${rounded.toFixed(decimals)}%`;
  return formatted;
}

/**
 * Small helper to decide color status given a percent value for UI use.
 * Returns 'positive' for >= 0, 'negative' for < 0, and 'neutral' for null/NA.
 *
 * @param {*} current
 * @param {*} previous
 * @returns {string} semantic status key
 */
export function percentChangeColorKey(current, previous) {
  const num = getPercentChangeNumber(current, previous);
  if (num === null) return "neutral";
  return num >= 0 ? "positive" : "negative";
}

// default export for convenience (optional)
const numberUtils = {
  toNumberOrNull,
  getPercentChangeNumber,
  formatPercentChange,
  percentChangeColorKey,
};

export default numberUtils;
